/**
 * 画笔/橡皮工具控制器（M2-11 / DESIGN.md §5.2）。
 *
 * 职责：把指针样本流喂给采样管线（M2-09），把 stamp 累积进笔画缓冲，
 * 维护一张文档空间的实时预览，收笔时经像素事务一次性合并——
 * 一笔 = 一条历史。选区存在时在合并前裁剪覆盖度（选区外画不上）。
 *
 * 指针样本用 getCoalescedEvents 展开：浏览器以显示刷新率派发 pointermove，
 * 但数位板的采样率更高，中间点都在 coalesced 列表里——不展开的话
 * 快速笔画会变成折线。
 */
import type { DocumentPoint } from '../viewport';
import { StrokeSampler, type InputPoint, type SamplerSettings } from '../../engine/brush/sampler';
import { StrokeBuffer, type BrushColor, type ToneRange } from '../../engine/brush/buffer';
import { layerId as makeLayerId, type LayerId } from '../../engine/tile/key';
import { ensureActiveRasterLayer } from '../../app/raster-target';
import type { AppStore } from '../../app/store';
import type { ToolManager } from './registry';

/** 一个指针样本（文档空间）。pressure 为 null 表示无压感设备。 */
export interface ToolSample extends DocumentPoint {
  readonly pressure: number | null;
  readonly timeMs: number;
}

/**
 * 从 PointerEvent 展开样本列表（coalesced 优先）。
 * 纯函数、鸭子类型入参——单元测试不需要真实 DOM 事件。
 */
export function samplesFromPointerEvent(
  event: {
    readonly pressure: number;
    readonly pointerType: string;
    readonly timeStamp: number;
    getCoalescedEvents?: () => ReadonlyArray<{
      readonly clientX: number;
      readonly clientY: number;
      readonly pressure: number;
      readonly timeStamp: number;
    }>;
    readonly clientX: number;
    readonly clientY: number;
  },
  toDocument: (client: { clientX: number; clientY: number }) => DocumentPoint,
): ToolSample[] {
  const usePressure = event.pointerType === 'pen';
  const coalesced = event.getCoalescedEvents?.() ?? [];
  const list = coalesced.length > 0 ? coalesced : [event];
  return list.map((e) => ({
    ...toDocument(e),
    pressure: usePressure ? e.pressure : null,
    timeMs: e.timeStamp,
  }));
}

export interface BrushDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  /** 触发一次画布重绘（预览更新后）。 */
  readonly requestRender: () => void;
  /** 前景色。取色器是后续任务，缺省黑。 */
  readonly color?: () => BrushColor;
}

export class BrushToolController {
  readonly #deps: BrushDeps;
  #sampler: StrokeSampler | null = null;
  #buffer: StrokeBuffer | null = null;
  #layer: LayerId | null = null;
  #mode: 'paint' | 'erase' | 'dodge' | 'burn' | 'saturate' | 'desaturate' | 'blur' | 'sharpen' | 'smudge' = 'paint';
  /** 减淡/加深的「范围」（阴影/中间调/高光）——只在这两个模式下有意义。 */
  #range: ToneRange = 'midtones';
  #opacity = 1;
  #preview: HTMLCanvasElement | null = null;
  #layerSeq = 0;
  /** 涂抹工具：起笔点与最新一次样本点（文档空间），收笔时求"起点−终点"当
   * 整笔画共用的取样偏移——没有逐 stamp 顺序相关的真实拖抹（见 buffer.ts
   * `mergeSmudgeInto` 的文档注释），这是静态近似需要的最小状态。 */
  #smudgeStart: { x: number; y: number } | null = null;
  #smudgeLast: { x: number; y: number } | null = null;

  constructor(deps: BrushDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#sampler !== null;
  }

  /** 处理指针。当前工具不是画笔/橡皮/减淡/加深/海绵时返回 false，让调用方交给别的工具。 */
  handlePointer(phase: 'down' | 'move' | 'up' | 'double', samples: readonly ToolSample[]): boolean {
    const tool = this.#deps.tools.active.id;
    if (
      tool !== 'brush' &&
      tool !== 'eraser' &&
      tool !== 'dodge' &&
      tool !== 'burn' &&
      tool !== 'sponge' &&
      tool !== 'blur' &&
      tool !== 'sharpen' &&
      tool !== 'smudge'
    ) {
      return false;
    }
    const document = this.#deps.store.document;
    if (document === null) return true; // 是我们的工具但没有文档，吞掉事件

    if (phase === 'down') {
      this.#begin(tool, document.size);
      const first = samples[0];
      if (first !== undefined) {
        this.#smudgeStart = { x: first.x, y: first.y };
        this.#smudgeLast = { x: first.x, y: first.y };
      }
    }
    if (this.#sampler === null) return true;

    if (phase === 'down' || phase === 'move') {
      for (const sample of samples) this.#feed(sample);
      const last = samples[samples.length - 1];
      if (last !== undefined) this.#smudgeLast = { x: last.x, y: last.y };
      this.#deps.requestRender();
    } else if (phase === 'up') {
      this.#commit();
    }
    return true;
  }

  /** 文档空间的实时预览（渲染桥在视口变换内调用）。 */
  drawPreview(ctx: CanvasRenderingContext2D): void {
    if (this.#preview === null || this.#buffer === null) return;
    ctx.save();
    ctx.globalAlpha = this.#opacity;
    if (this.#mode !== 'paint') {
      // 橡皮/减淡/加深/海绵预览：都是"这块的颜色/透明度会变"，不是真的
      // 往上画一层前景色，半透明的提示色示意足够，不需要各画一套语义
      ctx.globalAlpha = this.#opacity * 0.5;
    }
    ctx.drawImage(this.#preview, 0, 0);
    ctx.restore();
  }

  #begin(
    tool: 'brush' | 'eraser' | 'dodge' | 'burn' | 'sponge' | 'blur' | 'sharpen' | 'smudge',
    size: { width: number; height: number },
  ): void {
    const tools = this.#deps.tools;
    const diameter = tools.getOption(tool, 'size') as number;
    const hardness = (tools.getOption(tool, 'hardness') as number) / 100;
    this.#opacity = tool === 'brush' ? (tools.getOption('brush', 'opacity') as number) / 100 : 1;
    // 减淡/加深的"曝光度"、海绵/模糊/锐化/涂抹的"强度"跟画笔的"流量"是
    // 同一个数学角色：每个 stamp 往覆盖度缓冲里累积多少，笔画内多次经过
    // 继续累积——直接复用 flow 这条通道，不需要在 mergeDodgeInto/
    // mergeBurnInto/mergeSaturateInto/mergeBlurInto/mergeSharpenInto/
    // mergeSmudgeInto 里再叠一层强度参数
    const flow =
      tool === 'brush'
        ? (tools.getOption('brush', 'flow') as number) / 100
        : tool === 'dodge'
          ? (tools.getOption('dodge', 'exposure') as number) / 100
          : tool === 'burn'
            ? (tools.getOption('burn', 'exposure') as number) / 100
            : tool === 'sponge'
              ? (tools.getOption('sponge', 'flow') as number) / 100
              : tool === 'blur' || tool === 'sharpen' || tool === 'smudge'
                ? (tools.getOption(tool, 'strength') as number) / 100
                : 1;
    this.#mode =
      tool === 'eraser'
        ? 'erase'
        : tool === 'dodge'
          ? 'dodge'
          : tool === 'burn'
            ? 'burn'
            : tool === 'sponge'
              ? (tools.getOption('sponge', 'type') as 'saturate' | 'desaturate')
              : tool === 'blur' || tool === 'sharpen' || tool === 'smudge'
                ? tool
                : 'paint';
    if (tool === 'dodge' || tool === 'burn') {
      this.#range = tools.getOption(tool, 'range') as ToneRange;
    }

    const settings: SamplerSettings = {
      size: diameter,
      flow,
      spacing: 0.25,
      smoothing: 0.5,
      pressureSize: true,
      pressureFlow: true,
    };
    this.#sampler = new StrokeSampler(settings);
    this.#buffer = new StrokeBuffer(size.width, size.height, hardness);
    this.#layer = this.#targetLayer();

    // 无 DOM 环境（node 单测）没有预览画布——预览是纯视觉，不影响笔画语义
    if (typeof document !== 'undefined') {
      this.#preview ??= document.createElement('canvas');
      this.#preview.width = size.width;
      this.#preview.height = size.height;
      this.#preview.getContext('2d')?.clearRect(0, 0, size.width, size.height);
    }
  }

  #feed(sample: ToolSample): void {
    const sampler = this.#sampler;
    const buffer = this.#buffer;
    if (sampler === null || buffer === null) return;
    const input: InputPoint = {
      x: sample.x,
      y: sample.y,
      pressure: sample.pressure,
      timeMs: sample.timeMs,
    };
    const stamps = sampler.add(input);
    if (stamps.length === 0) return;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const stamp of stamps) {
      buffer.stamp(stamp);
      minX = Math.min(minX, stamp.x - stamp.size / 2);
      minY = Math.min(minY, stamp.y - stamp.size / 2);
      maxX = Math.max(maxX, stamp.x + stamp.size / 2);
      maxY = Math.max(maxY, stamp.y + stamp.size / 2);
    }
    this.#updatePreview(minX, minY, maxX, maxY);
  }

  #updatePreview(minX: number, minY: number, maxX: number, maxY: number): void {
    const buffer = this.#buffer;
    const preview = this.#preview;
    if (buffer === null || preview === null) return;
    const ctx = preview.getContext('2d');
    if (ctx === null) return;
    const x0 = Math.max(0, Math.floor(minX));
    const y0 = Math.max(0, Math.floor(minY));
    const x1 = Math.min(preview.width, Math.ceil(maxX));
    const y1 = Math.min(preview.height, Math.ceil(maxY));
    if (x0 >= x1 || y0 >= y1) return;

    // 预览色只是"这块会被怎么动"的提示，不是真的往上画一层前景色：
    // 减淡/橡皮变白、加深变黑、海绵/模糊/锐化/涂抹（不新增颜色，只是重新
    // 分布现有颜色）变中性灰，只有画笔本身用真实前景色。
    const previewColor =
      this.#mode === 'erase' || this.#mode === 'dodge'
        ? { r: 255, g: 255, b: 255 }
        : this.#mode === 'burn'
          ? { r: 0, g: 0, b: 0 }
          : this.#mode === 'saturate' ||
              this.#mode === 'desaturate' ||
              this.#mode === 'blur' ||
              this.#mode === 'sharpen' ||
              this.#mode === 'smudge'
            ? { r: 128, g: 128, b: 128 }
            : this.#color();
    const region = ctx.createImageData(x1 - x0, y1 - y0);
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) {
        const alpha = buffer.alphaAt(x, y);
        if (alpha <= 0) continue;
        const at = ((y - y0) * (x1 - x0) + (x - x0)) * 4;
        region.data[at] = previewColor.r;
        region.data[at + 1] = previewColor.g;
        region.data[at + 2] = previewColor.b;
        region.data[at + 3] = Math.round(alpha * 255);
      }
    }
    ctx.putImageData(region, x0, y0);
  }

  #commit(): void {
    const sampler = this.#sampler;
    const buffer = this.#buffer;
    const layer = this.#layer;
    const store = this.#deps.store;
    this.#sampler = null;
    if (sampler === null || buffer === null || layer === null) return;

    for (const stamp of sampler.finish()) buffer.stamp(stamp);

    // 选区裁剪：选区外的覆盖度归零（验收标准 3 的后半句）
    const selection = store.selection;
    if (selection !== null) buffer.clipToMask(selection);

    const label =
      this.#mode === 'erase'
        ? '橡皮擦'
        : this.#mode === 'dodge'
          ? '减淡'
          : this.#mode === 'burn'
            ? '加深'
            : this.#mode === 'saturate'
              ? '海绵（加色）'
              : this.#mode === 'desaturate'
                ? '海绵（去色）'
                : this.#mode === 'blur'
                  ? '模糊'
                  : this.#mode === 'sharpen'
                    ? '锐化'
                    : this.#mode === 'smudge'
                      ? '涂抹'
                      : '画笔';
    const transaction = store.beginPixelTransaction(label);
    if (this.#mode === 'dodge') {
      buffer.mergeDodgeInto(store.tiles, layer, transaction, this.#range);
    } else if (this.#mode === 'burn') {
      buffer.mergeBurnInto(store.tiles, layer, transaction, this.#range);
    } else if (this.#mode === 'saturate' || this.#mode === 'desaturate') {
      buffer.mergeSaturateInto(store.tiles, layer, transaction, this.#mode);
    } else if (this.#mode === 'blur') {
      buffer.mergeBlurInto(store.tiles, layer, transaction);
    } else if (this.#mode === 'sharpen') {
      buffer.mergeSharpenInto(store.tiles, layer, transaction);
    } else if (this.#mode === 'smudge') {
      // 整笔画共用一个偏移：起笔点相对终笔点的位移，取整（同仿制图章的
      // 教训——非整数偏移会让 sourceAt() 按小数下标读到 undefined）
      const start = this.#smudgeStart;
      const end = this.#smudgeLast;
      const offsetX = start !== null && end !== null ? Math.round(start.x - end.x) : 0;
      const offsetY = start !== null && end !== null ? Math.round(start.y - end.y) : 0;
      buffer.mergeSmudgeInto(store.tiles, layer, offsetX, offsetY, transaction);
    } else {
      buffer.mergeInto(store.tiles, layer, this.#color(), this.#opacity, transaction, this.#mode);
    }
    store.commitPixels(label, transaction.end());
    store.invalidatePixels([...buffer.tileCoords()]);

    this.#buffer = null;
    this.#smudgeStart = null;
    this.#smudgeLast = null;
    this.#preview?.getContext('2d')?.clearRect(0, 0, this.#preview.width, this.#preview.height);
    this.#deps.requestRender();
  }

  #color(): BrushColor {
    return this.#deps.color?.() ?? { r: 0, g: 0, b: 0 };
  }

  /** 目标图层：活动 raster（M3-01）；文档里没有 raster 就自动建一层。 */
  #targetLayer(): LayerId | null {
    return ensureActiveRasterLayer(this.#deps.store, () =>
      makeLayerId(`brush-layer-${++this.#layerSeq}-${this.#deps.store.document?.root.children.length ?? 0}`),
    );
  }
}
